import { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { updateCoil, listMasters } from "@/lib/api";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, Loader2, AlertCircle } from "lucide-react";

const Row = ({ id, label, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-semibold tracking-wide uppercase text-slate-600">
      {label}
    </Label>
    {children}
  </div>
);

const EditFGDialog = ({ coil, open, onOpenChange }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [coilIdError, setCoilIdError] = useState("");
  const queryClient = useQueryClient();

  useEffect(() => {
    if (coil) {
      setForm({
        coil_id: coil.coil_id || "",
        sku_name: coil.sku_name || "",
        grade: coil.grade || "",
        weight_kg: coil.weight_kg ?? "",
        production_date: coil.production_date || "",
      });
      setCoilIdError("");
    }
  }, [coil]);

  // Grades come from the FG SKU master (meta.grade of active FG SKUs)
  const { data: fgSkus = [] } = useQuery({
    queryKey: ["masters", "fg_sku", "active"],
    queryFn: () => listMasters({ master_type: "fg_sku", status: "active" }),
    enabled: open,
    staleTime: 60_000,
  });
  const masterGrades = useMemo(
    () => Array.from(new Set(fgSkus.map((m) => String(m.meta?.grade || "").trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [fgSkus],
  );
  const gradeOptions = useMemo(
    () => (form?.grade && !masterGrades.includes(form.grade) ? [form.grade, ...masterGrades] : masterGrades),
    [form?.grade, masterGrades],
  );

  if (!form) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const dirty = (
    form.coil_id !== (coil.coil_id || "")
    || form.sku_name !== (coil.sku_name || "")
    || form.grade !== (coil.grade || "")
    || Number(form.weight_kg) !== Number(coil.weight_kg)
    || form.production_date !== (coil.production_date || "")
  );

  const handleSave = async () => {
    setCoilIdError("");
    if (!form.coil_id.trim()) {
      setCoilIdError("FG ID cannot be empty");
      return;
    }
    setSaving(true);
    try {
      // Only send fields the server allows for FG kind.
      const patch = {
        coil_id: form.coil_id.trim(),
        sku_name: form.sku_name,
        grade: form.grade,
        weight_kg: Number(form.weight_kg),
        production_date: form.production_date,
      };
      await updateCoil(coil.id, patch);
      toast.success(`FG ${patch.coil_id} updated`);
      queryClient.invalidateQueries({ queryKey: ["coils"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 409 && typeof detail === "string" && detail.toLowerCase().includes("already exists")) {
        setCoilIdError(detail);
      } else {
        toast.error(detail || "Failed to update FG");
      }
    } finally {
      setSaving(false);
    }
  };

  const createdDisplay = coil.created_at ? String(coil.created_at).slice(0, 10) : "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-fg-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            Edit FG <span className="font-mono-data text-slate-700">{coil.coil_id}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Read-only reference block */}
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Created</p>
          <p data-testid="fg-ro-created" className="font-mono-data text-sm text-slate-900">{createdDisplay}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="sm:col-span-2">
            <Row id="fg-edit-id" label="FG ID">
              <Input
                id="fg-edit-id"
                data-testid="fg-edit-id"
                value={form.coil_id}
                onChange={(e) => { set("coil_id", e.target.value); setCoilIdError(""); }}
                className={`font-mono-data ${coilIdError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              />
              {coilIdError && (
                <p data-testid="fg-edit-id-error" className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {coilIdError}
                </p>
              )}
            </Row>
          </div>
          <Row id="fg-edit-sku" label="SKU Name">
            <Input id="fg-edit-sku" data-testid="fg-edit-sku" value={form.sku_name} onChange={(e) => set("sku_name", e.target.value)} />
          </Row>
          <Row id="fg-edit-grade" label="Grade">
            <Select value={form.grade} onValueChange={(v) => set("grade", v)}>
              <SelectTrigger data-testid="fg-edit-grade"><SelectValue placeholder="Select grade" /></SelectTrigger>
              <SelectContent>
                {gradeOptions.map((g) => (
                  <SelectItem key={g} value={g} data-testid={`fg-edit-grade-opt-${g}`}>{g}{masterGrades.includes(g) ? "" : " (not in FG master)"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Row>
          <Row id="fg-edit-weight" label="Net Weight (kg)">
            <Input id="fg-edit-weight" data-testid="fg-edit-weight" type="number" step="0.01" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} className="font-mono-data" />
          </Row>
          <Row id="fg-edit-production-date" label="Production Date">
            <Input id="fg-edit-production-date" data-testid="fg-edit-production-date" type="date" value={form.production_date || ""} onChange={(e) => set("production_date", e.target.value)} />
          </Row>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-fg-edit-btn" className="rounded-sm">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !dirty}
            data-testid="save-fg-edit-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-2" /> Save changes</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditFGDialog;
